import React, { useEffect, useRef, useState } from 'react';
import useReports from 'state/hooks/use-reports';
import useClassrooms from 'state/hooks/use-classrooms';
import ReportDownloadButton from '../common/ReportDownloadButton';
import BaseMatManModal from '@monorepo/shared/src/components/common/Modals/BaseMatManModal';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import ClassroomTreeView from 'components/common/ClassroomTreeView';
import Select from 'components/common/Select';
import CharacterReportingCard from './CharacterReportingCard';
import ReportFilterTag from '../common/ReportFilterTag';
import { useReactToPrint } from 'react-to-print';
import { faDownload, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { FONT_GSS_FUTURA_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import { getLetters } from '@monorepo/shared/src/util/text-util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import {
  FONT_GSS_FUTURA_BOOK,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/defaultTheme';

const t = (key: string, options?) => translate('reports', key, options);

const DownloadForMultipleStudents: React.FC = () => {
  const classes = useStyles();
  const [, { openModal, closeModal }] = useModal();
  const [{ progressData, selectedCharacter }] = useReports();
  const [{ activeClassroom }] = useClassrooms();
  const [isOpen, setIsOpen] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: reportRef });
  const letter = selectedCharacter && getLetters(selectedCharacter.character);
  const students = activeClassroom.students ?? [];
  const [selected, setSelected] = useState<number[]>(students.map((student) => student.id));
  const selectedStudents = students?.filter((item) => selected.includes(item?.id));
  const tagLabel =
    selectedStudents.length === activeClassroom?.students?.length
      ? t('selectedAllStudents')
      : t('selectedStudentsCount', { count: selectedStudents.length });

  const handleExit = () => {
    closeModal();
    setSelected(students.map((student) => student.id));
    setIsOpen(false);
  };

  const openStudentSelect = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    isOpen &&
      openModal({
        Title: () => <BaseModalTitle text={t('downloadForAllStudents')} onClose={handleExit} />,
        body: (
          <BaseMatManModal
            leftButtonProps={{
              onClick: handleExit,
              text: t('cancel'),
            }}
            rightButtonProps={{
              text: t('downloadFile'),
              startIcon: faDownload,
              onClick: () => {
                handlePrint();
                handleExit();
              },
            }}
          >
            <>
              <Typography className={classes.modalHeader}>
                {t('reportingForLetterForAllStudents', { letter })}
              </Typography>
              <Typography className={classes.modalText}>
                {t('reportingForLetterForAllStudentsSummary', { letter })}
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap="1rem">
                <Box width={'100%'}>
                  <Select multiple value={selected} renderValue={() => activeClassroom.name}>
                    <ClassroomTreeView
                      classroom={activeClassroom}
                      values={selected}
                      onChange={setSelected}
                    />
                  </Select>
                </Box>
                <ReportFilterTag label={tagLabel} icon={<FontAwesomeIcon icon={faUsers} />} />
              </Box>
              <div style={{ display: 'none' }}>
                <div ref={reportRef} className={classes.studentsContainer}>
                  <Typography className={classes.gridTitle}>
                    {t('reportingForLetter', { letter })}
                  </Typography>
                  {selectedStudents?.map((student) => (
                    <CharacterReportingCard
                      key={student.id}
                      student={student as any}
                      character={selectedCharacter as any}
                      progressData={progressData}
                    />
                  ))}
                </div>
              </div>
            </>
          </BaseMatManModal>
        ),
      });
  }, [isOpen, selected]);

  return (
    <ReportDownloadButton
      variant="outlined"
      text={t('downloadForAllStudents')}
      onClick={openStudentSelect}
    />
  );
};

const useStyles = makeStyles(() => ({
  gridTitle: {
    fontSize: remCalc(32),
    lineHeight: remCalc(48),
    fontFamily: FONT_GSS_FUTURA_BOLD,
  },
  modalHeader: {
    fontSize: remCalc(24),
    lineHeight: remCalc(36),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
  modalText: {
    fontSize: remCalc(18),
    lineHeight: remCalc(28),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_BOOK,
  },
  studentsContainer: {
    display: 'block',
    '& > *': {
      margin: '1rem',
    },
  },
}));

export default DownloadForMultipleStudents;
